import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaAt,
  FaMapMarkerAlt,
  FaCity,
  FaGlobe,
  FaCalendarAlt,
  FaVenusMars,
  FaCamera,
  FaSave,
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationCircle,
  FaUserEdit,
} from "react-icons/fa";

import { logoutUser } from "../../redux/slices/authSlice";
import { updateUserProfile } from "../../redux/apis/normalUser/updateUserProfile";

const ProfileEditorPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const authUser = useSelector((state) => state.auth);
  const user = authUser?.user;
  const token = authUser?.token;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contact: "",
    username: "",
    village: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    dob: "",
    gender: "",
    profilePic: null,
  });

  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  // --------------------------------------------------
  // Load user data
  // --------------------------------------------------
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        contact: user.contact || "",
        username: user.username || "",
        village: user.village || "",
        city: user.city || "",
        state: user.state || "",
        postalCode: user.postalCode || "",
        country: user.country || "",
        dob: user.dob ? user.dob.split("T")[0] : "",
        gender: user.gender || "",
        profilePic: user.profilePic || null,
      });

      setPreviewUrl(user.profilePic || "");
    }
  }, [user]);

  // --------------------------------------------------
  // Profile image preview
  // --------------------------------------------------
  useEffect(() => {
    if (
      formData.profilePic &&
      typeof formData.profilePic !== "string" &&
      formData.profilePic instanceof File
    ) {
      const objectUrl = URL.createObjectURL(formData.profilePic);

      setPreviewUrl(objectUrl);

      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }

    if (!formData.profilePic && user?.profilePic) {
      setPreviewUrl(user.profilePic);
    }
  }, [formData.profilePic, user]);

  // --------------------------------------------------
  // Handle input changes
  // --------------------------------------------------
  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const selectedFile = files?.[0] || null;

      setFormData((prev) => ({
        ...prev,
        profilePic: selectedFile,
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setMessage("");
  };

  // --------------------------------------------------
  // Submit
  // --------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user?._id) {
      setMessage("User information not found.");
      return;
    }

    setIsSubmitting(true);
    setMessage("");

    const submitData = new FormData();

    // Always send username
    submitData.append("username", formData.username || user.username || "");

    // Append only changed fields
    for (const key in formData) {
      if (
        key !== "username" &&
        key !== "profilePic" &&
        formData[key] &&
        formData[key] !== user[key]
      ) {
        submitData.append(key, formData[key]);
      }
    }

    // Append new profile picture only
    if (formData.profilePic && formData.profilePic instanceof File) {
      submitData.append("profilePic", formData.profilePic);
    }

    // Check whether anything actually changed
    if ([...submitData.entries()].length <= 1) {
      setMessage("No changes to update.");
      setIsSubmitting(false);
      return;
    }

    try {
      await dispatch(
        updateUserProfile({
          userId: user._id,
          formData: submitData,
          token,
        }),
      ).unwrap();

      setMessage("Profile updated successfully. Logging out...");

      setTimeout(() => {
        dispatch(logoutUser());
        navigate("/login");
      }, 2000);
    } catch (err) {
      const errorMessage =
        err?.payload || err?.message || "Update failed. Please try again.";

      setMessage(
        typeof errorMessage === "string"
          ? errorMessage
          : "Update failed. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // --------------------------------------------------
  // Form fields
  // --------------------------------------------------
  const fields = [
    {
      label: "Full Name",
      name: "name",
      type: "text",
      icon: FaUser,
      placeholder: "Enter your full name",
    },
    {
      label: "Email",
      name: "email",
      type: "email",
      icon: FaEnvelope,
      placeholder: "Enter your email",
    },
    {
      label: "Contact Number",
      name: "contact",
      type: "tel",
      icon: FaPhone,
      placeholder: "Enter contact number",
    },
    {
      label: "Username",
      name: "username",
      type: "text",
      icon: FaAt,
      placeholder: "Enter username",
    },
    {
      label: "Village",
      name: "village",
      type: "text",
      icon: FaMapMarkerAlt,
      placeholder: "Enter village",
    },
    {
      label: "City",
      name: "city",
      type: "text",
      icon: FaCity,
      placeholder: "Enter city",
    },
    {
      label: "State",
      name: "state",
      type: "text",
      icon: FaMapMarkerAlt,
      placeholder: "Enter state",
    },
    {
      label: "Postal Code",
      name: "postalCode",
      type: "text",
      icon: FaMapMarkerAlt,
      placeholder: "Enter postal code",
    },
    {
      label: "Country",
      name: "country",
      type: "text",
      icon: FaGlobe,
      placeholder: "Enter country",
    },
  ];

  return (
    <>
      {/* ==================================================
          GLOBAL STYLES
      ================================================== */}
      <style>
        {`
          *,
          *::before,
          *::after {
            box-sizing: border-box;
          }

          .profile-input,
          .profile-select {
            width: 100%;
            max-width: 100%;
            min-width: 0;
            min-height: 46px;
            border: 1px solid rgb(203 213 225);
            border-radius: 12px;
            background: rgba(255, 255, 255, 0.95);
            color: rgb(15 31 51);
            outline: none;
            transition:
              border-color 0.2s ease,
              box-shadow 0.2s ease,
              background-color 0.2s ease;
          }

          .profile-input {
            padding: 11px 13px 11px 44px;
            font-size: 16px;
          }

          .profile-select {
            padding: 11px 13px 11px 44px;
            font-size: 16px;
          }

          .profile-input:focus,
          .profile-select:focus {
            border-color: rgb(14 165 233);
            box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.12);
            background: white;
          }

          .profile-input::placeholder {
            color: rgb(148 163 184);
          }

          .profile-file {
            width: 100%;
            max-width: 100%;
            min-width: 0;
            border: 1px dashed rgb(125 211 252);
            border-radius: 14px;
            background: rgba(240, 249, 255, 0.7);
            padding: 12px;
            font-size: 14px;
            color: rgb(51 65 85);
          }

          .profile-file::file-selector-button {
            border: 0;
            border-radius: 9px;
            background: rgb(14 165 233);
            color: white;
            padding: 8px 12px;
            margin-right: 10px;
            cursor: pointer;
            font-weight: 600;
          }

          .profile-scroll::-webkit-scrollbar {
            width: 7px;
          }

          .profile-scroll::-webkit-scrollbar-track {
            background: transparent;
          }

          .profile-scroll::-webkit-scrollbar-thumb {
            background: rgb(148 163 184);
            border-radius: 999px;
          }

          @media (min-width: 640px) {
            .profile-input,
            .profile-select {
              font-size: 14px;
            }
          }
        `}
      </style>

      {/* ==================================================
          PAGE
      ================================================== */}
      <div className="relative min-h-[100dvh] overflow-x-hidden bg-slate-100">
        {/* Background decorations */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="
              absolute
              -left-24
              -top-24
              h-64
              w-64
              rounded-full
              bg-sky-200/30
              blur-3xl
              sm:h-80
              sm:w-80
            "
          />

          <div
            className="
              absolute
              -bottom-32
              -right-24
              h-80
              w-80
              rounded-full
              bg-blue-300/20
              blur-3xl
              sm:h-96
              sm:w-96
            "
          />

          <div
            className="
              absolute
              left-1/2
              top-1/3
              h-40
              w-40
              -translate-x-1/2
              rounded-full
              bg-cyan-200/10
              blur-3xl
              sm:h-64
              sm:w-64
            "
          />
        </div>

        {/* ==================================================
            MAIN CONTAINER
        ================================================== */}
        <div
          className="
            relative
            z-10
            flex
            min-h-[100dvh]
            items-start
            justify-center
            p-2
            sm:p-4
            md:p-6
            lg:items-center
            lg:p-8
          "
        >
          <div
            className="
              flex
              w-full
              max-w-6xl
              flex-col
              overflow-hidden
              rounded-2xl
              border
              border-white/80
              bg-white/90
              shadow-2xl
              backdrop-blur-xl
              sm:rounded-3xl
              lg:max-h-[calc(100dvh-4rem)]
              lg:min-h-[650px]
              lg:flex-row
            "
          >
            {/* ==================================================
                LEFT INFORMATION PANEL
            ================================================== */}
            <aside
              className="
                hidden
                w-full
                shrink-0
                flex-col
                justify-between
                overflow-y-auto
                bg-[linear-gradient(145deg,rgb(15,31,51)_0%,rgb(26,58,92)_50%,rgb(30,73,118)_100%)]
                p-6
                text-white
                lg:flex
                lg:w-[36%]
                lg:p-7
                xl:w-[38%]
                xl:p-8
              "
            >
              <div>
                {/* Logo / Brand */}
                <div className="mb-8 flex items-center gap-3">
                  <div
                    className="
                      flex
                      h-11
                      w-11
                      shrink-0
                      items-center
                      justify-center
                      overflow-hidden
                      rounded-xl
                      bg-white
                      shadow-lg
                    "
                  >
                    <img
                      src="/logo.jpeg"
                      alt="MRHealth"
                      className="h-full w-full object-cover"
                    />
                  </div>

                  <div className="min-w-0">
                    <p className="text-lg font-bold tracking-tight">MRHealth</p>

                    <p className="text-xs text-sky-200">Healthcare Together</p>
                  </div>
                </div>

                {/* Heading */}
                <div className="mb-8">
                  <div
                    className="
                      mb-4
                      inline-flex
                      items-center
                      gap-2
                      rounded-full
                      border
                      border-sky-300/20
                      bg-sky-400/10
                      px-3
                      py-1.5
                      text-xs
                      font-semibold
                      text-sky-200
                    "
                  >
                    <FaUserEdit />

                    <span>Profile Settings</span>
                  </div>

                  <h1
                    className="
                      text-2xl
                      font-bold
                      leading-tight
                      tracking-tight
                      xl:text-3xl
                    "
                  >
                    Keep your profile
                    <span className="block text-sky-300">up to date</span>
                  </h1>

                  <p className="mt-4 max-w-md text-sm leading-6 text-slate-300">
                    Update your personal information so your healthcare profile
                    stays accurate and useful.
                  </p>
                </div>

                {/* Benefits */}
                <div className="space-y-3">
                  {[
                    {
                      icon: FaUser,
                      title: "Personal Information",
                      text: "Maintain your basic profile details.",
                    },
                    {
                      icon: FaMapMarkerAlt,
                      title: "Location Details",
                      text: "Keep your location information current.",
                    },
                    {
                      icon: FaCamera,
                      title: "Profile Picture",
                      text: "Use a clear profile image.",
                    },
                    {
                      icon: FaSave,
                      title: "Secure Updates",
                      text: "Changes are securely submitted.",
                    },
                  ].map((item) => {
                    const Icon = item.icon;

                    return (
                      <div
                        key={item.title}
                        className="
                          flex
                          items-start
                          gap-3
                          rounded-xl
                          border
                          border-white/10
                          bg-white/5
                          p-3
                          transition
                          hover:bg-white/10
                        "
                      >
                        <div
                          className="
                            flex
                            h-9
                            w-9
                            shrink-0
                            items-center
                            justify-center
                            rounded-lg
                            bg-sky-400/15
                            text-sky-300
                          "
                        >
                          <Icon className="text-sm" />
                        </div>

                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold text-white">
                            {item.title}
                          </h3>

                          <p className="mt-0.5 text-xs leading-5 text-slate-400">
                            {item.text}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Bottom quote */}
              <div
                className="
                  mt-8
                  rounded-xl
                  border
                  border-white/10
                  bg-white/5
                  p-4
                "
              >
                <p className="text-sm font-medium text-sky-200">
                  वैद्यो नारायणो हरिः
                </p>

                <p className="mt-1 text-xs text-slate-400">
                  “The healer is a form of God.”
                </p>
              </div>
            </aside>

            {/* ==================================================
                RIGHT CONTENT
            ================================================== */}
            <main
              className="
                flex
                min-w-0
                flex-1
                flex-col
                bg-white
              "
            >
              {/* ==================================================
                  HEADER
              ================================================== */}
              <header
                className="
                  shrink-0
                  border-b
                  border-slate-200
                  bg-white
                  px-4
                  py-4
                  sm:px-6
                  sm:py-5
                  lg:px-8
                "
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="
                        flex
                        h-10
                        w-10
                        shrink-0
                        items-center
                        justify-center
                        rounded-xl
                        border
                        border-slate-200
                        bg-white
                        text-slate-600
                        shadow-sm
                        transition
                        hover:border-sky-200
                        hover:bg-sky-50
                        hover:text-sky-600
                        active:scale-95
                      "
                      aria-label="Go back"
                    >
                      <FaArrowLeft className="text-sm" />
                    </button>

                    <div className="min-w-0">
                      <h2
                        className="
                          truncate
                          text-lg
                          font-bold
                          tracking-tight
                          text-slate-800
                          sm:text-xl
                        "
                      >
                        Edit Profile
                      </h2>

                      <p className="truncate text-xs text-slate-500 sm:text-sm">
                        Update your personal information
                      </p>
                    </div>
                  </div>

                  {/* Desktop badge */}
                  <div
                    className="
                      hidden
                      shrink-0
                      items-center
                      gap-2
                      rounded-full
                      border
                      border-sky-100
                      bg-sky-50
                      px-3
                      py-1.5
                      text-xs
                      font-semibold
                      text-sky-700
                      sm:flex
                    "
                  >
                    <FaUserEdit />

                    <span>Profile</span>
                  </div>
                </div>
              </header>

              {/* ==================================================
                  FORM AREA
              ================================================== */}
              <div
                className="
                  profile-scroll
                  flex-1
                  overflow-visible
                  lg:min-h-0
                  lg:overflow-y-auto
                "
              >
                <div
                  className="
                    mx-auto
                    w-full
                    max-w-4xl
                    px-3
                    py-5
                    sm:px-5
                    sm:py-6
                    md:px-7
                    lg:px-8
                    xl:py-7
                  "
                >
                  {/* ==================================================
                      MOBILE PROFILE HEADER
                  ================================================== */}
                  <section
                    className="
                      mb-5
                      rounded-2xl
                      border
                      border-sky-100
                      bg-[linear-gradient(135deg,#f0f9ff,#ffffff)]
                      p-4
                      shadow-sm
                      sm:p-5
                    "
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="
                          h-16
                          w-16
                          shrink-0
                          overflow-hidden
                          rounded-2xl
                          border-2
                          border-white
                          bg-sky-100
                          shadow-md
                          sm:h-20
                          sm:w-20
                        "
                      >
                        {previewUrl ? (
                          <img
                            src={previewUrl}
                            alt="Profile preview"
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <div
                            className="
                              flex
                              h-full
                              w-full
                              items-center
                              justify-center
                              bg-sky-100
                              text-sky-600
                            "
                          >
                            <FaUser className="text-2xl" />
                          </div>
                        )}
                      </div>

                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wider text-sky-600">
                          Your Profile
                        </p>

                        <h3 className="mt-1 truncate text-base font-bold text-slate-800 sm:text-lg">
                          {formData.name || "Your Name"}
                        </h3>

                        <p className="truncate text-xs text-slate-500 sm:text-sm">
                          @{formData.username || "username"}
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* ==================================================
                      PERSONAL INFORMATION
                  ================================================== */}
                  <section
                    className="
                      mb-5
                      overflow-hidden
                      rounded-2xl
                      border
                      border-slate-200
                      bg-white
                      shadow-sm
                    "
                  >
                    {/* Section header */}
                    <div
                      className="
                        border-b
                        border-slate-100
                        bg-slate-50/80
                        px-4
                        py-3.5
                        sm:px-5
                        sm:py-4
                      "
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="
                            flex
                            h-9
                            w-9
                            shrink-0
                            items-center
                            justify-center
                            rounded-xl
                            bg-sky-100
                            text-sky-600
                          "
                        >
                          <FaUser className="text-sm" />
                        </div>

                        <div className="min-w-0">
                          <h3 className="text-sm font-bold text-slate-800 sm:text-base">
                            Personal Information
                          </h3>

                          <p className="text-[11px] text-slate-500 sm:text-xs">
                            Your basic personal details
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Fields */}
                    <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:p-5">
                      {fields.map(
                        ({ label, name, type, icon: Icon, placeholder }) => (
                          <div key={name} className="min-w-0">
                            <label
                              htmlFor={name}
                              className="
                                mb-1.5
                                block
                                text-xs
                                font-semibold
                                text-slate-700
                                sm:text-sm
                              "
                            >
                              {label}
                            </label>

                            <div className="relative min-w-0">
                              <Icon
                                className="
                                  pointer-events-none
                                  absolute
                                  left-3.5
                                  top-1/2
                                  z-10
                                  -translate-y-1/2
                                  text-sm
                                  text-slate-400
                                "
                              />

                              <input
                                id={name}
                                type={type}
                                name={name}
                                value={formData[name]}
                                onChange={handleChange}
                                placeholder={placeholder}
                                className="profile-input"
                              />
                            </div>
                          </div>
                        ),
                      )}

                      {/* DOB */}
                      <div className="min-w-0">
                        <label
                          htmlFor="dob"
                          className="
                            mb-1.5
                            block
                            text-xs
                            font-semibold
                            text-slate-700
                            sm:text-sm
                          "
                        >
                          Date of Birth
                        </label>

                        <div className="relative min-w-0">
                          <FaCalendarAlt
                            className="
                              pointer-events-none
                              absolute
                              left-3.5
                              top-1/2
                              z-10
                              -translate-y-1/2
                              text-sm
                              text-slate-400
                            "
                          />

                          <input
                            id="dob"
                            type="date"
                            name="dob"
                            value={formData.dob}
                            onChange={handleChange}
                            className="profile-input"
                          />
                        </div>
                      </div>

                      {/* Gender */}
                      <div className="min-w-0">
                        <label
                          htmlFor="gender"
                          className="
                            mb-1.5
                            block
                            text-xs
                            font-semibold
                            text-slate-700
                            sm:text-sm
                          "
                        >
                          Gender
                        </label>

                        <div className="relative min-w-0">
                          <FaVenusMars
                            className="
                              pointer-events-none
                              absolute
                              left-3.5
                              top-1/2
                              z-10
                              -translate-y-1/2
                              text-sm
                              text-slate-400
                            "
                          />

                          <select
                            id="gender"
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                            className="profile-select"
                          >
                            <option value="">Select gender</option>

                            <option value="Male">Male</option>

                            <option value="Female">Female</option>

                            <option value="Other">Other</option>

                            <option value="Prefer not to say">
                              Prefer not to say
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* ==================================================
                      PROFILE PICTURE
                  ================================================== */}
                  <section
                    className="
                      mb-5
                      overflow-hidden
                      rounded-2xl
                      border
                      border-slate-200
                      bg-white
                      shadow-sm
                    "
                  >
                    <div
                      className="
                        border-b
                        border-slate-100
                        bg-slate-50/80
                        px-4
                        py-3.5
                        sm:px-5
                        sm:py-4
                      "
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="
                            flex
                            h-9
                            w-9
                            shrink-0
                            items-center
                            justify-center
                            rounded-xl
                            bg-blue-100
                            text-blue-600
                          "
                        >
                          <FaCamera className="text-sm" />
                        </div>

                        <div className="min-w-0">
                          <h3 className="text-sm font-bold text-slate-800 sm:text-base">
                            Profile Picture
                          </h3>

                          <p className="text-[11px] text-slate-500 sm:text-xs">
                            Upload JPG or PNG image
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 sm:p-5">
                      <div
                        className="
                          flex
                          flex-col
                          gap-4
                          sm:flex-row
                          sm:items-center
                        "
                      >
                        {/* Preview */}
                        <div
                          className="
                            flex
                            h-24
                            w-24
                            shrink-0
                            items-center
                            justify-center
                            overflow-hidden
                            rounded-2xl
                            border
                            border-sky-100
                            bg-sky-50
                          "
                        >
                          {previewUrl ? (
                            <img
                              src={previewUrl}
                              alt="Profile preview"
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <FaUser className="text-3xl text-sky-400" />
                          )}
                        </div>

                        {/* Upload */}
                        <div className="min-w-0 flex-1">
                          <label
                            htmlFor="profilePic"
                            className="
                              mb-2
                              block
                              text-xs
                              font-semibold
                              text-slate-700
                              sm:text-sm
                            "
                          >
                            Choose a new picture
                          </label>

                          <input
                            id="profilePic"
                            type="file"
                            name="profilePic"
                            accept="image/jpeg,image/png"
                            onChange={handleChange}
                            className="profile-file"
                          />

                          {formData.profilePic &&
                            formData.profilePic instanceof File && (
                              <p
                                className="
                                  mt-2
                                  break-words
                                  text-xs
                                  text-slate-500
                                "
                              >
                                Selected:{" "}
                                <span className="font-medium text-slate-700">
                                  {formData.profilePic.name}
                                </span>
                              </p>
                            )}

                          <p className="mt-2 text-[11px] leading-5 text-slate-400">
                            Recommended: square image with a clear face. Maximum
                            file size should follow your backend upload limit.
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* ==================================================
                      MESSAGE
                  ================================================== */}
                  {message && (
                    <div
                      className={`
                        mb-5
                        flex
                        items-start
                        gap-3
                        rounded-2xl
                        border
                        p-3.5
                        text-sm
                        ${
                          message.includes("successfully")
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-red-200 bg-red-50 text-red-700"
                        }
                      `}
                    >
                      <div className="mt-0.5 shrink-0">
                        {message.includes("successfully") ? (
                          <FaCheckCircle />
                        ) : (
                          <FaExclamationCircle />
                        )}
                      </div>

                      <p className="min-w-0 break-words leading-5">{message}</p>
                    </div>
                  )}

                  {/* ==================================================
                      ACTIONS
                  ================================================== */}
                  <div
                    className="
                      flex
                      flex-col-reverse
                      gap-3
                      pb-2
                      sm:flex-row
                      sm:justify-end
                    "
                  >
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      disabled={isSubmitting}
                      className="
                        flex
                        min-h-11
                        w-full
                        items-center
                        justify-center
                        gap-2
                        rounded-xl
                        border
                        border-slate-200
                        bg-white
                        px-5
                        py-2.5
                        text-sm
                        font-semibold
                        text-slate-700
                        shadow-sm
                        transition
                        hover:border-sky-200
                        hover:bg-sky-50
                        hover:text-sky-700
                        disabled:cursor-not-allowed
                        disabled:opacity-50
                        sm:w-auto
                      "
                    >
                      <FaArrowLeft className="text-xs" />

                      <span>Cancel</span>
                    </button>

                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="
                        flex
                        min-h-11
                        w-full
                        items-center
                        justify-center
                        gap-2
                        rounded-xl
                        bg-[linear-gradient(135deg,rgb(14,165,233),rgb(30,73,118))]
                        px-6
                        py-2.5
                        text-sm
                        font-bold
                        text-white
                        shadow-lg
                        shadow-sky-900/10
                        transition
                        hover:-translate-y-0.5
                        hover:shadow-xl
                        active:translate-y-0
                        disabled:cursor-not-allowed
                        disabled:opacity-60
                        disabled:hover:translate-y-0
                        sm:w-auto
                      "
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="
                              h-4
                              w-4
                              animate-spin
                              rounded-full
                              border-2
                              border-white/30
                              border-t-white
                            "
                          />

                          <span>Updating...</span>
                        </>
                      ) : (
                        <>
                          <FaSave className="text-xs" />

                          <span>Update Profile</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfileEditorPage;
